import React, { lazy, memo } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, PaletteIcon, TypeIcon, ImageIcon, SparklesIcon, TargetIcon, TrendingUpIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function BrandingService() {
  const features = [{
    icon: PaletteIcon,
    title: 'Logo Design',
    description: 'Unique, memorable logos that represent your brand'
  }, {
    icon: TypeIcon,
    title: 'Typography',
    description: 'Custom font selections that match your personality'
  }, {
    icon: ImageIcon,
    title: 'Brand Colors',
    description: 'Strategic color palettes that evoke emotion'
  }, {
    icon: SparklesIcon,
    title: 'Style Guide',
    description: 'Comprehensive brand guidelines for consistency'
  }, {
    icon: TargetIcon,
    title: 'Brand Strategy',
    description: 'Clear positioning to stand out from competitors'
  }, {
    icon: TrendingUpIcon,
    title: 'Marketing Assets',
    description: 'Business cards, social media, and more'
  }];
  const benefits = ['Stand out from your competition', 'Build trust with customers', 'Create a memorable impression', 'Consistent brand experience', 'Professional brand materials', 'Scalable brand system'];
  const process = [{
    step: '01',
    title: 'Discovery',
    description: 'We learn about your business, values, and target audience to understand what makes you unique.'
  }, {
    step: '02',
    title: 'Research',
    description: 'We analyze your industry, competitors, and market trends to position your brand effectively.'
  }, {
    step: '03',
    title: 'Design',
    description: 'We create multiple brand concepts with logos, colors, and typography for your review.'
  }, {
    step: '04',
    title: 'Refine',
    description: 'We work with you to perfect your chosen direction with unlimited revisions included.'
  }, {
    step: '05',
    title: 'Deliver',
    description: 'You receive all brand files, guidelines, and assets ready to use across all platforms.'
  }];
  return <>
      <Helmet data-id="element-1867">
        <title data-id="element-1868">Brand Identity Design | Bright Pixxel Studios – UK</title>
        <meta name="description" content="Professional brand identity design for UK businesses. Logos, color palettes, and brand guidelines that make you stand out." data-id="element-1869" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-1870">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1871">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1872">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-1873">
              <PaletteIcon size={40} className="text-[#0DC3DC]" data-id="element-1874" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-1875">
              Brand Identity Design
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-1876">
              Create a memorable brand that resonates with your audience and
              sets you apart from the competition.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1877">
              <Link to="/pricing" className="bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-lg" data-id="element-1878">
                Get a Free Quote
              </Link>
              <Link to="/portfolio" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0C1B26] px-8 py-3 rounded-lg font-medium transition-colors" data-id="element-1879">
                See Our Work
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Features Grid */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-1880">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1881">
          <div className="max-w-6xl mx-auto" data-id="element-1882">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-1883">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1884">
                Complete Brand Identity
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1885">
                Everything you need to build a strong, cohesive brand
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1886">
              {features.map((feature, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl hover:shadow-lg transition-shadow" data-id="element-1887">
                  <div className="w-12 h-12 bg-[#0DC3DC]/30 rounded-lg flex items-center justify-center mb-4 border border-[#0DC3DC]/40" data-id="element-1888">
                    <feature.icon size={24} className="text-[#0DC3DC]" data-id="element-1889" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-1890">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1891">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Section with Image */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1892">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1893">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1894">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-1895">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1896">
                Why Brand Identity Matters
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-1897">
                Your brand is more than just a logo. It's the complete
                experience your customers have with your business.
              </p>
              <div className="space-y-4" data-id="element-1898">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-center gap-3" data-id="element-1899">
                    <div className="w-6 h-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center flex-shrink-0" data-id="element-1900">
                      <CheckIcon size={14} className="text-[#0DC3DC]" data-id="element-1901" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-1902">
                      {benefit}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-xl overflow-hidden shadow-xl" data-id="element-1903">
              <img src="https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Brand identity design workspace with color swatches and logo sketches - Bright Pixxel Studios professional branding process" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-1904" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Process Section */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-1905">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1906">
          <div className="max-w-4xl mx-auto" data-id="element-1907">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-1908">
              Our Branding Process
            </motion.h2>
            <div className="space-y-8" data-id="element-1909">
              {process.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="flex gap-6" data-id="element-1910">
                  <div className="flex-shrink-0" data-id="element-1911">
                    <div className="w-16 h-16 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-1912">
                      <span className="text-2xl font-bold text-[#0DC3DC]" data-id="element-1913">
                        {item.step}
                      </span>
                    </div>
                  </div>
                  <div className="flex-grow" data-id="element-1914">
                    <h3 className="text-xl font-bold mb-2" data-id="element-1915">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300" data-id="element-1916">
                      {item.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-1917">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1918">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1919">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-1920">
              Ready to Build Your Brand?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-white/90 mb-8" data-id="element-1921">
              Let's create a brand identity that truly represents your business
              and resonates with your customers.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-1922">
              <a href="mailto:info@bright-pixxel.com?subject=New%20Enquiry%20-%20Branding" className="inline-block bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-4 rounded-lg font-bold transition-colors shadow-lg text-lg" data-id="element-1923">
                Get Your Free Quote
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}